import type { ResidentDefaultFormValues } from '@/features/resident/types'

export type ReimbursementDocumentType =
  | 'signed_lease'
  | 'current_ledger'
  | 'final_ledger'
  | 'original_ledger'
  | 'skip_affidavit'
  | 'eviction_lawsuit'
  | 'alternate_court_document'
  | 'cares_act_notice'
  | 'bankruptcy_document'
  | 'court_registry'
  | 'stipulation_agreement'
  | 'bond_registry_receipt'
  | 'default_stipulation'
  | 'original_lease'

export type ReimbursementDocumentReviewStatus =
  | 'pending'
  | 'uploaded'
  | 'approved'
  | 'incomplete'
  /** @deprecated Prefer `incomplete` — kept for legacy local/test data */
  | 'rejected'

export interface ReimbursementDocumentFile {
  id?: string
  file: File | null
  fileName: string
  fileSize: number
  fileUrl?: string
  uploadedAt?: Date
  uploadedBy?: string
  status?: ReimbursementDocumentReviewStatus
  reviewedAt?: Date
  reviewedBy?: string
  rejectionReason?: string
  reclassifiedFrom?: string
  reclassifiedAt?: Date
  reclassifiedBy?: string
  previousFileName?: string
  previousFileSize?: number
  previousFileUrl?: string
  previousRejectionReason?: string
  previousReviewedAt?: Date
  previousReviewedBy?: string
}

export type ReimbursementWorkflowProgress =
  | 'info_needed'
  | 'action_needed'
  | 'awaiting_final_info'
  | 'ready_for_review'
  | 'in_review'
  | 'denied'
  | 'paid'
  | 'expired'
  | 'cancelled'

export type ReimbursementDocumentTrigger = 'awaiting' | 'uploaded' | 'docs_approved'

/** 3.0 reimbursement request scalars (synced with `reimbursement` columns via API). */
export interface ReimbursementExtendedFields {
  lease_modifications_at_move_in: boolean | null
  security_deposit_collected: boolean | null
  cares_act_applicable: boolean | null
  skipped_during_eviction: boolean | null
  alternate_court_document_applicable: boolean | null
  using_third_party_eviction_services: boolean | null
  utilizing_legal_team_for_eviction: boolean | null
  third_party_servicer_name: string
  bankruptcy_applicable: boolean | null
  appeal_applicable: boolean | null
  resident_paid_into_court: boolean | null
  stipulation_applicable: boolean | null
  resident_defaulted_on_repayment: boolean | null
  payment_info_confirmed: boolean
}

export const EMPTY_REIMBURSEMENT_EXTENDED_FIELDS: ReimbursementExtendedFields = {
  lease_modifications_at_move_in: null,
  security_deposit_collected: false,
  cares_act_applicable: null,
  skipped_during_eviction: null,
  alternate_court_document_applicable: null,
  using_third_party_eviction_services: null,
  utilizing_legal_team_for_eviction: null,
  third_party_servicer_name: '',
  bankruptcy_applicable: null,
  appeal_applicable: null,
  resident_paid_into_court: null,
  stipulation_applicable: null,
  resident_defaulted_on_repayment: null,
  payment_info_confirmed: false,
}

export type ReimbursementDocumentsMap = Record<
  ReimbursementDocumentType,
  ReimbursementDocumentFile | null
>

export function createEmptyReimbursementDocumentsMap(): ReimbursementDocumentsMap {
  return {
    signed_lease: null,
    current_ledger: null,
    final_ledger: null,
    original_ledger: null,
    skip_affidavit: null,
    eviction_lawsuit: null,
    alternate_court_document: null,
    cares_act_notice: null,
    bankruptcy_document: null,
    court_registry: null,
    stipulation_agreement: null,
    bond_registry_receipt: null,
    default_stipulation: null,
    original_lease: null,
  }
}

export const DOCUMENT_TYPE_LABELS: Record<ReimbursementDocumentType, string> = {
  original_lease: 'Liberty Rent Approved Lease Agreement (prior to unit transfer)',
  signed_lease: 'Final Lease Agreement',
  current_ledger: 'Current Ledger',
  final_ledger: 'Final Ledger after Deposit Accounting',
  original_ledger: 'Ledger for Original Lease',
  skip_affidavit: 'Skip Affidavit',
  eviction_lawsuit: 'Eviction Lawsuit',
  alternate_court_document: 'Alternate Court-Issued Document',
  cares_act_notice: '30-Day Notice to Vacate',
  bankruptcy_document: 'Bankruptcy Document',
  court_registry: 'Appeal Document',
  bond_registry_receipt: 'Court Registry (Rent Bond)',
  stipulation_agreement: 'Stipulation Agreement',
  default_stipulation: 'Default Stipulation',
}

export interface ReimbursementFormLogicInput {
  values: ResidentDefaultFormValues
  extended: ReimbursementExtendedFields
  documents: ReimbursementDocumentsMap
}

export type ReimbursementMilestoneId = 1 | 2 | 3

/** `true` = PMC cannot edit that milestone until Liberty unlocks. */
export type ReimbursementSectionLockState = Record<ReimbursementMilestoneId, boolean>

export const DEFAULT_REIMBURSEMENT_SECTION_LOCK_STATE: ReimbursementSectionLockState = {
  1: false,
  2: false,
  3: false,
}
